/**
 * Directly using crypto will throw error
 * "Uncaught Error: Module "crypto" has been externalized for browser compatibility."
 *
 * NodeJS and the Browser both have a module called crypto that implements the webcrypto standard. Those modules are compatible, but need to be accessed differently. In NodeJS, it can be used directly but in the browser it is provided by the window context that does not exist in NodeJS.
 * @link https://stackoverflow.com/a/76188480/8186765
 */

enum ALGO {
  SHA = "SHA-1",
  SHA_256 = "SHA-256",
  SHA_384 = "SHA-384",
  SHA_512 = "SHA-512",
}

/**
 * Encrypts any string passed. Defaults to SHA-256 encryption
 * @returns string
 * @param token string
 * @param algo ALGO
 */
export default async function hash(token: string, algo: ALGO = ALGO.SHA_256) {
  const compatibleCrypto = getCrypto();

  const data = new TextEncoder().encode(token);
  /** [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest) */
  const byteHash = await compatibleCrypto.subtle.digest(algo, data);

  const arrayHash = Array.from(new Uint8Array(byteHash));
  const hexHash = arrayHash
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .toLocaleUpperCase();

  return hexHash;
}

function getCrypto() {
  try {
    return window.crypto;
  } catch {
    return crypto;
  }
}
